import { IProductDetail } from '@/api/types/public';
import logger from '@/utils/client-logger';
import { ELensType, EPrescriptionType } from '@/utils/enums/goods';
import { useTranslations } from '@/i18n/use-translations';
import { useMemo } from 'react';

export interface LensesAndCoatingsData {
  showBlueLight: boolean;
  showColorTint: boolean;
  showPhotochromic: boolean;
  showDriving: boolean;
  showProg: boolean;
  showReading: boolean;
  availableItems: string[];
  showThisModule: boolean;
}

export const useLensesAndCoatings = (detail: IProductDetail): LensesAndCoatingsData => {
  const t = useTranslations('goodsDetail.lensesAndCoatings');

  // 判断是否显示蓝光阻隔
  const showBlueLight = useMemo(() => {
    const { lens_type = [] } = detail;
    return (
      Array.isArray(lens_type) && lens_type.some((type) => type === ELensType.BLUE_LIGHT_BLOCKING)
    );
  }, [detail]);

  // 判断是否显示彩色镜片
  const showColorTint = useMemo(() => {
    const { lens_type = [] } = detail;
    return Array.isArray(lens_type) && lens_type.some((type) => type === ELensType.COLOR_TINT);
  }, [detail]);

  // 判断是否显示感光变色镜片
  const showPhotochromic = useMemo(() => {
    const { lens_type = [] } = detail;
    return (
      Array.isArray(lens_type) && lens_type.some((type) => type === ELensType.PHOTOCHROMIC_LENSES)
    );
  }, [detail]);

  // 判断是否显示驾驶镜片
  const showDriving = useMemo(() => {
    const { lens_type = [] } = detail;
    return Array.isArray(lens_type) && lens_type.some((type) => type === ELensType.DRIVING_LENSES);
  }, [detail]);

  // 判断是否显示渐进镜片
  const showProg = useMemo(() => {
    const { prescription_type = [] } = detail;
    return (
      Array.isArray(prescription_type) &&
      prescription_type.some((type) => type === ELensType.PROGRESSIVE)
    );
  }, [detail]);

  // 判断是否显示阅读镜片
  const showReading = useMemo(() => {
    const { prescription_type = [] } = detail;
    return (
      Array.isArray(prescription_type) &&
      prescription_type.some((type) => type === EPrescriptionType.READING_GLASSES)
    );
  }, [detail]);

  // 计算可用项目列表
  // 排序：UV Protection > 适用镜片类型 > Anti-Scratch Coating
  const availableItems = useMemo(() => {
    const { lens_type = [], uv_coating = [] } = detail;
    const uvProtectionItems: string[] = [];
    const lensTypeItems: string[] = [];
    const antiScratchItems: string[] = [];

    try {
      // 处理 uv_coating 数组，分类为 UV Protection 和 Anti-Scratch Coating
      if (Array.isArray(uv_coating) && uv_coating.length > 0) {
        uv_coating.forEach((coating) => {
          if (coating === ELensType.UV_PROTECTION) {
            // UV Protection 或其他涂层（保持向后兼容）
            uvProtectionItems.push(t('uvProtection'));
          }
          // 判断是 UV Protection 还是 Anti-Scratch Coating
          if (coating === ELensType.ANTI_SCRATCH_COATING) {
            antiScratchItems.push(t('antiScratchCoating'));
          }
        });
      }
    } catch (error) {
      logger.error(error);
    }

    // 添加适用镜片类型
    // 使用字符串包含判断，而不是精确匹配
    if (
      Array.isArray(lens_type) &&
      lens_type.some((type) => String(type).includes(ELensType.BLUE_LIGHT_BLOCKING))
    ) {
      lensTypeItems.push(t('types.blueLight'));
    }
    if (
      Array.isArray(lens_type) &&
      lens_type.some((type) => String(type).includes(ELensType.COLOR_TINT))
    ) {
      lensTypeItems.push(t('types.colorTint'));
    }
    if (
      Array.isArray(lens_type) &&
      lens_type.some((type) => String(type).includes(ELensType.PHOTOCHROMIC))
    ) {
      lensTypeItems.push(t('types.photochromic'));
    }
    if (
      Array.isArray(lens_type) &&
      lens_type.some((type) => String(type).includes(ELensType.DRIVING_LENSES))
    ) {
      lensTypeItems.push(t('types.driving'));
    }
    if (
      Array.isArray(lens_type) &&
      lens_type.some((type) => String(type).includes(ELensType.POLARIZED_SUNGLASS_LENSES))
    ) {
      lensTypeItems.push(t('types.polarized'));
    }

    // 按照顺序合并：UV Protection > 适用镜片类型 > Anti-Scratch Coating
    return [...uvProtectionItems, ...lensTypeItems, ...antiScratchItems];
  }, [
    detail,
    t,
    showBlueLight,
    showProg,
    showColorTint,
    showPhotochromic,
    showDriving,
    showReading,
  ]);

  // 判断是否显示此模块
  const showThisModule = useMemo(() => {
    const { is_optical_frame, product_lens, is_show_add_to_cart } = detail;

    // 如果配置的加购按钮为add to card，此模块不展示
    if (is_show_add_to_cart === 1) {
      return false;
    }

    // 仅网站商品分类为"镜框/光学镜"的商品，展示此模块
    if (is_optical_frame === 2) {
      return false;
    }

    // 不支持配镜，不展示此模块
    if (product_lens === 2) {
      return false;
    }

    // lens_type包含六者中的任何一个就可以展示此模块
    return availableItems.length > 0;
  }, [
    detail,
    availableItems,
    showBlueLight,
    showProg,
    showColorTint,
    showPhotochromic,
    showDriving,
    showReading,
  ]);

  return {
    showBlueLight,
    showColorTint,
    showPhotochromic,
    showDriving,
    showProg,
    showReading,
    availableItems,
    showThisModule,
  };
};
